import React, { Component } from 'react';
import { Row, Col, Card, Form, Button } from 'react-bootstrap';
import { Formik } from 'formik';
import { connect } from 'react-redux';
import moment from 'moment';
import XLSX from 'sheetjs-style';
import PropTypes from 'prop-types';
import Layout from './DashboardLayout';
import Loading from '../components/Loader';
import {
  MemberMultiSelectDropdown,
  MultiSelectItemDropdown,
} from '../components/common';
import { teamService, clientsService, userService } from '../services';
import EmployeeDevicesDataTable from '../components/reports/EmployeeDevicesDataTable';

class EmployeeDevices extends Component {
  state = {
    isLoading: false,
    isUpdatingProjectsList: false,
    teamMembersList: [],
    projectsList: [],
    clientsData: [],
    site: [],
    clientId: [],
    project: [],
    changeSource: null,
    employeeDevicesData: [],
  };

  employeeDevicesFormRef = React.createRef();

  teamMemberFormRef = React.createRef();

  projectSelectInputRef = React.createRef();

  clientSelectInputRef = React.createRef();

  siteSelectInputRef = React.createRef();

  pageHeading = {
    preTitle: 'Reports',
    title: 'Employee Devices',
  };

  componentDidMount() {
    this.getClientData();
    this.getMembersAndProjects();
    this.handleGenerateData();
  }

  componentDidUpdate(prevProps, prevState) {
    const { changeSource } = this.state;

    if (!changeSource || changeSource === prevState.changeSource) return;

    const formik = this.employeeDevicesFormRef.current;
    const memberSelect = this.teamMemberFormRef?.current?.select;
    const clientSelect = this.clientSelectInputRef?.current?.select;
    const projectSelect = this.projectSelectInputRef?.current?.select;

    const clearSelect = (selectRef) => selectRef?.clearValue();
    const resetFields = (fields) =>
      fields.forEach((f) => formik.setFieldValue(f, []));

    switch (changeSource) {
      case 'site':
        resetFields(['clientId', 'project', 'teamMember']);
        [memberSelect, clientSelect, projectSelect].forEach(clearSelect);
        this.getClientData();
        setTimeout(() => {
          this.getMembersAndProjects();
        }, 200);
        break;

      case 'client':
        resetFields(['teamMember', 'project']);
        [memberSelect, projectSelect].forEach(clearSelect);
        this.getMembersAndProjects();
        break;

      case 'project':
        resetFields(['teamMember']);
        clearSelect(memberSelect);
        this.getMembersWithProject();
        break;

      default:
        break;
    }

    this.setState({ changeSource: null });
  }

  getClientData = () => {
    const { site } = this.employeeDevicesFormRef.current.values;
    this.setState({ isLoading: true });
    const clientParams = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      status: 'active',
      siteId: site,
    };
    if (site.length === 0 || site.includes('all')) {
      delete clientParams.siteId;
    }
    clientsService
      .get_client_list(clientParams)
      .then((clientData) => {
        if (clientData.data) {
          this.setState({ clientsData: [...clientData.data] });
        }
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  getMembersAndProjects = () => {
    this.setState({ isUpdatingProjectsList: true, isLoading: true });
    const { site, clientId } = this.employeeDevicesFormRef.current.values;
    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      siteId: site,
      clientId,
    };
    if (site.length === 0 || site.includes('all')) {
      delete params.siteId;
    }
    if (clientId.length === 0 || clientId.includes('all')) {
      delete params.clientId;
    }
    teamService
      .get_team_members_and_projects(params)
      .then((result) => {
        const { teamMembers, projects } = result.data;
        this.setState({
          projectsList: [...projects],
          teamMembersList: [...teamMembers],
          isUpdatingProjectsList: false,
        });
      })
      .catch(() => {
        this.setState({ isUpdatingProjectsList: false });
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  getMembersWithProject = () => {
    const { site, clientId, project } = this.state;
    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      summaryList: true,
      siteId: site,
      clientId,
    };
    if (project.length > 0) {
      params.projectId = project;
    }
    if (project.length === 0 || project.includes('all')) {
      delete params.projectId;
    }
    if (site.length === 0 || site.includes('all')) {
      delete params.siteId;
    }
    if (clientId.length === 0 || clientId.includes('all')) {
      delete params.clientId;
    }

    this.setState({ isLoading: true });
    teamService
      .get_team_members_with_project(params)
      .then((response) => {
        const { members } = response && response.data && response.data[0];
        this.setState({ teamMembersList: [...members] });
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  handleSiteChange = (selectedSites) => {
    this.setState({
      site: selectedSites,
      isUpdatingProjectsList: true,
      changeSource: 'site',
    });
  };

  handleClientChange = (selectedClient) => {
    this.setState({
      clientId: selectedClient,
      changeSource: 'client',
    });
  };

  handleProjectChange = (selectedProjectList) => {
    this.setState({
      changeSource: 'project',
      project: selectedProjectList,
    });
  };

  handleSubmitForm = (params) => {
    this.setState({
      site: params.site,
      clientId: params.clientId,
      project: params.project,
    });
    this.handleGenerateData(params);
  };

  handleGenerateData = (formdata = {}) => {
    this.setState({ isLoading: true });

    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      teamMemberId: formdata.teamMember || [],
      siteId: formdata.site || [],
      clientId: formdata.clientId || [],
      projectId: formdata.project || [],
    };
    if (
      params.teamMemberId.length === 0 ||
      params.teamMemberId.includes('all')
    ) {
      delete params.teamMemberId;
    }
    if (params.siteId.length === 0 || params.siteId.includes('all')) {
      delete params.siteId;
    }
    if (params.clientId.length === 0 || params.clientId.includes('all')) {
      delete params.clientId;
    }
    if (params.projectId.length === 0 || params.projectId.includes('all')) {
      delete params.projectId;
    }

    userService
      .get_employee_devices(params)
      .then((response) => {
        if (response && response.data) {
          const employeeDevicesData = response.data.map((row) => ({
            employeeId: row.EmployeeId || '',
            name: row.lastName
              ? `${row.firstName} ${row.lastName}`
              : row.firstName || '',
            email: row.email || '',
            uuid: row.uuid || '',
            localIp: row.localIpAddress || '',
            publicIp: row.publicIpAddress || '',
            project:
              row.projects && row.projects.length > 0
                ? row.projects.join(', ')
                : '',
          }));
          this.setState({ employeeDevicesData });
        }
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  handleExportExcel = () => {
    const { employeeDevicesData } = this.state;
    const workbook = XLSX.utils.book_new();
    const headers = [
      'Employee Id',
      'Name',
      'Email',
      'UUID',
      'Local IP',
      'Public IP',
      'Project',
    ];
    const worksheetData = [
      ['Employee Devices'],
      headers,
      ...employeeDevicesData.map((row) => [
        row.employeeId,
        row.name,
        row.email,
        row.uuid,
        row.localIp,
        row.publicIp,
        row.project,
      ]),
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(
      workbook,
      `Employee_Devices_${moment().format('YYYYMMDD_HHmmss')}.xlsx`
    );
  };

  render() {
    const {
      isLoading,
      teamMembersList,
      projectsList,
      clientsData,
      isUpdatingProjectsList,
      employeeDevicesData,
    } = this.state;

    const { hierarchy, permissions } = this.props;

    return (
      <Layout pageHeading={this.pageHeading}>
        <Formik
          initialValues={{
            site: [],
            clientId: [],
            project: [],
            teamMember: [],
          }}
          onSubmit={(values) => {
            this.handleSubmitForm(values);
          }}
          innerRef={this.employeeDevicesFormRef}
        >
          {(formikprops) => (
            <Form onSubmit={formikprops.handleSubmit} className="mb-5">
              <Row>
                {hierarchy?.sites?.length > 0 && (
                  <Col md={6} lg={3}>
                    <MultiSelectItemDropdown
                      id="siteSelectInputRef"
                      name="site"
                      labelText="Sites"
                      fromArray={hierarchy.sites.map((site) => ({
                        value: site._id,
                        label: site.siteName,
                      }))}
                      formProps={formikprops}
                      isDisabled={hierarchy.sites.length < 1 && true}
                      innerRef={this.siteSelectInputRef}
                      isAllItemOption
                      isAllItemValuesWithNoArray
                      showErrorMessage
                      onChangeAction={this.handleSiteChange}
                    />
                  </Col>
                )}
                {permissions &&
                  permissions.includes('client_manage') &&
                  clientsData?.length > 0 && (
                    <Col md={6} lg={3}>
                      <MultiSelectItemDropdown
                        id="clientSelectInputRef"
                        name="clientId"
                        labelText="Clients"
                        fromArray={clientsData.map((client) => ({
                          value: client._id,
                          label: client.name,
                        }))}
                        formProps={formikprops}
                        isDisabled={clientsData.length < 1 && true}
                        innerRef={this.clientSelectInputRef}
                        isAllItemOption
                        isAllItemValuesWithNoArray
                        showErrorMessage
                        onChangeAction={this.handleClientChange}
                      />
                    </Col>
                  )}
                <Col md={6} lg={3}>
                  <MultiSelectItemDropdown
                    id="projectSelectInputRef"
                    name="project"
                    labelText="Projects"
                    fromArray={projectsList.map((project) => ({
                      value: project._id,
                      label: project.title,
                    }))}
                    formProps={formikprops}
                    isDisabled={projectsList.length < 1 && true}
                    innerRef={this.projectSelectInputRef}
                    isAllItemOption
                    isAllItemValuesWithNoArray
                    showErrorMessage
                    onChangeAction={this.handleProjectChange}
                  />
                </Col>
                <Col md={6} lg={3}>
                  <MemberMultiSelectDropdown
                    id="teamMemberFormRef"
                    name="teamMember"
                    labelText="Team Member(s)"
                    fromArray={teamMembersList}
                    formProps={formikprops}
                    isDisabled={teamMembersList.length < 1 && true}
                    innerRef={this.teamMemberFormRef}
                    isAllTeamMemberOption
                    isAllTeamMemberValuesWithNoArray
                    requiredValueAs="teamMember"
                    showErrorMessage
                  />
                </Col>
                <Col xs={12}>
                  <Form.Group controlId="generateData">
                    <div>
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={isUpdatingProjectsList || isLoading}
                      >
                        Generate
                      </Button>
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
        <Card style={{ marginBottom: '0px !important' }}>
          <Card.Header>
            <Row className="align-items-center my-3">
              <Col>
                <div className="card-title h4 m-0">
                  {this.pageHeading.title}
                </div>
              </Col>
              {employeeDevicesData && employeeDevicesData.length > 0 && (
                <Col xs="auto" className="ml-auto">
                  <Button
                    variant="white"
                    type="button"
                    size="sm"
                    className="mx-1"
                    onClick={this.handleExportExcel}
                  >
                    Download as Excel
                  </Button>
                </Col>
              )}
            </Row>
          </Card.Header>
          <EmployeeDevicesDataTable
            isLoading={isLoading}
            employeeDevicesData={employeeDevicesData}
          />
        </Card>
        {isLoading && <Loading contentAreaOnly />}
      </Layout>
    );
  }
}

const mapStateToProps = (state) => {
  const { hierarchy, permissionsReducer } = state;
  return {
    hierarchy,
    permissions: permissionsReducer,
  };
};

EmployeeDevices.propTypes = {
  hierarchy: PropTypes.object,
  permissions: PropTypes.object,
};

export default connect(mapStateToProps)(EmployeeDevices);
